import type { Channel, ChannelFilter } from "../types/contract";

export const CHANNEL_META: Record<ChannelFilter, { label: string; c: string; bg: string }> = {
  all: { label: "すべて", c: "var(--ch-all-c)", bg: "var(--ch-all-bg)" },
  shopify: { label: "Shopify直販", c: "var(--ch-shopify-c)", bg: "var(--ch-shopify-bg)" },
  rakuten: { label: "楽天", c: "var(--ch-rakuten-c)", bg: "var(--ch-rakuten-bg)" },
  line: { label: "LINE登録", c: "var(--ch-line-c)", bg: "var(--ch-line-bg)" },
  instagram: { label: "インスタフォロワー", c: "var(--ch-instagram-c)", bg: "var(--ch-instagram-bg)" },
  other: { label: "未分類", c: "var(--ch-other-c)", bg: "var(--ch-other-bg)" },
  // 2026-10-23: Amazon 広告のキャンペーン行・ダッシュボードカード用（タブには出さない）
  amazon: { label: "Amazon", c: "var(--ch-amazon-c)", bg: "var(--ch-amazon-bg)" },
};

/** ダッシュボードのチャネルカード状態バッジ（v2追加: state="ok"|"no_data"|"ads_off"|"not_connected"）。 */
export const CHANNEL_STATE_META: Record<
  "no_data" | "ads_off" | "not_connected",
  { label: string; c: string; bg: string }
> = {
  no_data: { label: "データ欠損", c: "var(--warn)", bg: "var(--warn-bg)" },
  ads_off: { label: "広告停止中", c: "var(--ink-sub)", bg: "var(--ch-all-bg)" },
  not_connected: { label: "未連携", c: "var(--ink-faint)", bg: "var(--ch-all-bg)" },
};

export const CHANNEL_TABS: ChannelFilter[] = ["all", "shopify", "rakuten", "line", "instagram", "other"];

export function channelMeta(channel: Channel | ChannelFilter) {
  return CHANNEL_META[channel] ?? CHANNEL_META.other;
}

const CAMPAIGN_STATUS_META: Record<string, { label: string; c: string; bg: string }> = {
  ACTIVE: { label: "配信中", c: "var(--st-active-c)", bg: "var(--st-active-bg)" },
  PAUSED: { label: "一時停止", c: "var(--st-paused-c)", bg: "var(--st-paused-bg)" },
  PENDING_REVIEW: { label: "審査中", c: "var(--st-pending-c)", bg: "var(--st-pending-bg)" },
  ARCHIVED: { label: "終了", c: "var(--st-ended-c)", bg: "var(--st-ended-bg)" },
  DELETED: { label: "削除済み", c: "var(--st-ended-c)", bg: "var(--st-ended-bg)" },
};

export function campaignStatusMeta(status: string) {
  return (
    CAMPAIGN_STATUS_META[status] ?? {
      label: status,
      c: "var(--ink-sub)",
      bg: "var(--ch-all-bg)",
    }
  );
}

// ── 媒体（Meta / Google / Amazon・2026-10-09, Amazon は 2026-10-23） ──────────────────────────────────────
export type PlatformFilter = "all" | "meta" | "google" | "amazon";
export const PLATFORM_TABS: PlatformFilter[] = ["all", "meta", "google", "amazon"];
export const PLATFORM_META: Record<PlatformFilter, { label: string; c: string; bg: string }> = {
  all: { label: "すべての媒体", c: "var(--ch-all-c)", bg: "var(--ch-all-bg)" },
  meta: { label: "Meta", c: "var(--pf-meta-c)", bg: "var(--pf-meta-bg)" },
  google: { label: "Google", c: "var(--pf-google-c)", bg: "var(--pf-google-bg)" },
  amazon: { label: "Amazon", c: "var(--pf-amazon-c)", bg: "var(--pf-amazon-bg)" },
};

/** Google の advertising_channel_type → 日本語。未知の値はそのまま表示（隠さない）。 */
const GOOGLE_CHANNEL_TYPE_LABELS: Record<string, string> = {
  SEARCH: "検索",
  SHOPPING: "ショッピング",
  PERFORMANCE_MAX: "P-MAX",
  DEMAND_GEN: "デマンドジェネレーション",
  DISPLAY: "ディスプレイ",
  VIDEO: "動画",
  MULTI_CHANNEL: "アプリ",
};
export function googleChannelTypeLabel(t: string | undefined | null): string {
  if (!t) return "—";
  return GOOGLE_CHANNEL_TYPE_LABELS[t] ?? t;
}

/** Google の campaign.primary_status（配信実態）→ 補助バッジ。ELIGIBLE/PAUSED は通常ステータスと重複するので出さない。 */
const GOOGLE_PRIMARY_STATUS_META: Record<string, { label: string; c: string; bg: string }> = {
  PENDING: { label: "開始待ち", c: "var(--st-pending-c)", bg: "var(--st-pending-bg)" },
  NOT_ELIGIBLE: { label: "配信不可", c: "var(--warn)", bg: "var(--warn-bg)" },
  LIMITED: { label: "配信制限あり", c: "var(--warn)", bg: "var(--warn-bg)" },
  LEARNING: { label: "学習中", c: "var(--st-pending-c)", bg: "var(--st-pending-bg)" },
  MISCONFIGURED: { label: "設定不備", c: "var(--warn)", bg: "var(--warn-bg)" },
  ENDED: { label: "終了", c: "var(--st-ended-c)", bg: "var(--st-ended-bg)" },
  REMOVED: { label: "削除済み", c: "var(--st-ended-c)", bg: "var(--st-ended-bg)" },
};
export function googlePrimaryStatusMeta(s: string | null | undefined): { label: string; c: string; bg: string } | null {
  if (!s || s === "ELIGIBLE" || s === "PAUSED") return null;
  return GOOGLE_PRIMARY_STATUS_META[s] ?? { label: s, c: "var(--ink-sub)", bg: "var(--ch-all-bg)" };
}

/** Google の Ad Strength（広告の有効性）。GOOD/EXCELLENT が推奨ライン（塩田さん 2026-10-09）。 */
const GOOGLE_STRENGTH_META: Record<string, { label: string; c: string; bg: string; ok: boolean }> = {
  EXCELLENT: { label: "有効性 非常に高い", c: "var(--st-active-c)", bg: "var(--st-active-bg)", ok: true },
  GOOD: { label: "有効性 高い", c: "var(--st-active-c)", bg: "var(--st-active-bg)", ok: true },
  AVERAGE: { label: "有効性 平均的", c: "var(--warn)", bg: "var(--warn-bg)", ok: false },
  POOR: { label: "有効性 低い", c: "var(--warn)", bg: "var(--warn-bg)", ok: false },
  PENDING: { label: "有効性 判定待ち", c: "var(--ink-sub)", bg: "var(--ch-all-bg)", ok: false },
};
export function googleStrengthMeta(s: string | null | undefined): { label: string; c: string; bg: string; ok: boolean } {
  if (!s) return { label: "有効性 未判定", c: "var(--ink-faint)", bg: "var(--ch-all-bg)", ok: false };
  return GOOGLE_STRENGTH_META[s] ?? { label: `有効性 ${s}`, c: "var(--ink-sub)", bg: "var(--ch-all-bg)", ok: false };
}

/** Google 広告の種別（ad_type）→ 日本語。 */
const GOOGLE_AD_TYPE_LABELS: Record<string, string> = {
  RESPONSIVE_SEARCH_AD: "レスポンシブ検索広告",
  SHOPPING_PRODUCT_AD: "ショッピング広告",
  RESPONSIVE_DISPLAY_AD: "レスポンシブディスプレイ広告",
  VIDEO_RESPONSIVE_AD: "動画広告",
  DEMAND_GEN_MULTI_ASSET_AD: "デマンドジェネレーション広告",
  ASSET_GROUP: "P-MAX アセットグループ",
};
export function googleAdTypeLabel(t: string | undefined | null): string {
  if (!t) return "広告";
  return GOOGLE_AD_TYPE_LABELS[t] ?? t;
}

/** Google 広告の審査状態（policy_summary.approval_status）→ ラベル。APPROVED は出さない。 */
const GOOGLE_APPROVAL_LABELS: Record<string, string> = {
  APPROVED_LIMITED: "承認（制限付き）",
  DISAPPROVED: "不承認",
  UNDER_REVIEW: "審査中",
  AREA_OF_INTEREST_ONLY: "一部地域のみ",
};
export function googleApprovalLabel(s: string | null | undefined): string | null {
  if (!s || s === "APPROVED" || s === "ELIGIBLE") return null;
  return GOOGLE_APPROVAL_LABELS[s] ?? s;
}

/** Amazon の広告種別（ad_product）→ 略称バッジ（SP/SB/SD）。未知の値はそのまま表示（隠さない）。 */
const AMAZON_AD_PRODUCT_LABELS: Record<string, { short: string; long: string }> = {
  SPONSORED_PRODUCTS: { short: "SP", long: "スポンサープロダクト" },
  SPONSORED_BRANDS: { short: "SB", long: "スポンサーブランド" },
  SPONSORED_DISPLAY: { short: "SD", long: "スポンサーディスプレイ" },
};
export function amazonAdProductLabel(p: string | undefined | null): { short: string; long: string } {
  if (!p) return { short: "—", long: "—" };
  return AMAZON_AD_PRODUCT_LABELS[p] ?? { short: p, long: p };
}

export function yen(n: number): string {
  return "¥" + Math.round(n).toLocaleString("ja-JP");
}
